import { c as _c } from "react/compiler-runtime";
import { useRequestErrorToast } from '@onefootprint/hooks';
import { IcoLock24, IcoLockOpen24 } from '@onefootprint/icons';
import type { Organization } from '@onefootprint/request-types/dashboard';
import { Divider, Toggle, Tooltip } from '@onefootprint/ui';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import PermissionGate from '@/components/permission-gate';
import useUpdateOrg from '@/hooks/use-update-org';
import createStringList from '@/utils/create-string-list';
export type DomainAccessProps = {
  org: Organization;
};
const DomainAccess = t0 => {
  const $ = _c(40);
  const {
    org
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "invite.allowDomainAccess"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("onboarding", t1);
  const updateOrgMutation = useUpdateOrg();
  const showRequestErrorToast = useRequestErrorToast();
  const [allowDomainAccess, setAllowDomainAccess] = useState(org.allowDomainAccess);
  const disableToggle = !!org.isDomainAlreadyClaimed && !allowDomainAccess;
  let t2;
  if ($[1] !== allowDomainAccess || $[2] !== showRequestErrorToast || $[3] !== updateOrgMutation) {
    t2 = async () => {
      ;
      try {
        await updateOrgMutation.mutateAsync({
          body: {
            allowDomainAccess: !allowDomainAccess
          }
        });
      } catch (t3) {
        const error = t3;
        showRequestErrorToast(error);
      }
      setAllowDomainAccess(!allowDomainAccess);
    };
    $[1] = allowDomainAccess;
    $[2] = showRequestErrorToast;
    $[3] = updateOrgMutation;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleToggle = t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      bold: <span className="text-label-2" />
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== org.domains) {
    t4 = createStringList(org.domains);
    $[6] = org.domains;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = <Trans i18nKey={_temp} components={t3} values={{
      domain: t4
    }} ns="onboarding" />;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== allowDomainAccess || $[11] !== disableToggle || $[12] !== handleToggle || $[13] !== t5) {
    t6 = <Toggle defaultChecked={false} disabled={disableToggle} checked={allowDomainAccess} label={t5} size="compact" onChange={handleToggle} />;
    $[10] = allowDomainAccess;
    $[11] = disableToggle;
    $[12] = handleToggle;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const toggle = t6;
  if (!org.domains) {
    return null;
  }
  let t7;
  if ($[15] !== allowDomainAccess) {
    t7 = allowDomainAccess ? <IcoLockOpen24 /> : <IcoLock24 />;
    $[15] = allowDomainAccess;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t) {
    t8 = t(_temp2);
    $[17] = t;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t8) {
    t9 = <h3 className="text-label-2">{t8}</h3>;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t7 || $[22] !== t9) {
    t10 = <div className="flex gap-2 items-center self-stretch">{t7}{t9}</div>;
    $[21] = t7;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t) {
    t11 = t(_temp3);
    $[24] = t;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t11) {
    t12 = <h4 className="text-body-2 text-secondary">{t11}</h4>;
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t10 || $[29] !== t12) {
    t13 = <div className="flex flex-col gap-3 items-start">{t10}{t12}</div>;
    $[28] = t10;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Divider />;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== disableToggle || $[33] !== org.domains || $[34] !== t || $[35] !== toggle) {
    t15 = <div className="overflow-hidden">{disableToggle ? <Tooltip content={t(_temp4, {
        domain: createStringList(org.domains)
      })}>{toggle}</Tooltip> : <PermissionGate fallbackText={t(_temp5)} scopeKind="org_settings">{toggle}</PermissionGate>}</div>;
    $[32] = disableToggle;
    $[33] = org.domains;
    $[34] = t;
    $[35] = toggle;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== t13 || $[38] !== t15) {
    t16 = <div className="flex flex-col gap-4 items-start self-stretch p-4 mt-4 rounded border border-solid border-tertiary" data-testid="domain-access">{t13}{t14}{t15}</div>;
    $[37] = t13;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  return t16;
};
export default DomainAccess;
function _temp($) {
  return $.invite.allowDomainAccess.toggle.label;
}
function _temp2($_0) {
  return $_0.title;
}
function _temp3($_1) {
  return $_1.subtitle;
}
function _temp4($_2) {
  return $_2.toggle.domainAlreadyClaimed;
}
function _temp5($_3) {
  return $_3.toggle.ctaNotAllowed;
}